import { ANIMAL_SOUNDS, isAnimal } from '@platform/schema/cast';
import { figureOf, sceneVisualSchema } from '@platform/schema/content';

import type { CastEntry, CastId } from '@platform/schema/cast';
import type { Figure, LessonCast } from '@platform/schema/content';

export type Display = Figure['kind'];
export type Party = Extract<Figure, { kind: 'situation' }>['cast'][number];

export { figureOf };
export type { Figure };

/** A character seated as the lesson casts them: the name under the figure is the role. */
export const partyOf = (member: LessonCast[number]): Party => ({
  name: member.role,
  character: member.character,
});

/** A scene with no figure yet, seating the lesson's first cast member. */
export const seatFigure = (cast: LessonCast, headline: string): Figure => {
  const [first] = cast;
  return {
    kind: 'situation',
    caption: headline.slice(0, 120),
    cast: first === undefined ? [] : [partyOf(first)],
  };
};

const YOU: Party = { name: 'You', you: true };

/**
 * Speech and situation hold the same people. Thoughts carry across both ways; spoken lines
 * do not, because a situation has nowhere to put them — the draft shows the loss before
 * Save, and Undo puts them back.
 */
export function withDisplay(figure: Figure, display: Display): Figure {
  if (figure.kind === display) return figure;
  const { caption, props } = figure;
  const kept = props === undefined ? {} : { props };

  if (figure.kind === 'dialogue') {
    const thoughtOf = (by: 'left' | 'right') =>
      figure.lines.find((line) => line.by === by && line.thought === true)
        ?.text;
    const seat = (by: 'left' | 'right'): Party => {
      const thought = thoughtOf(by);
      return thought === undefined ? figure[by] : { ...figure[by], thought };
    };
    return {
      kind: 'situation',
      caption,
      cast: [seat('left'), seat('right')],
      ...kept,
    };
  }

  const [left = YOU, right = YOU] = figure.cast.map(
    ({ thought: _thought, ...party }) => party,
  );
  const sides = ['left', 'right'] as const;
  const lines = figure.cast
    .slice(0, 2)
    .flatMap((party, index) =>
      party.thought === undefined
        ? []
        : [{ by: sides[index] ?? 'left', text: party.thought, thought: true }],
    );

  return {
    kind: 'dialogue',
    caption,
    left,
    right,
    lines: lines.length > 0 ? lines : [{ by: 'left', text: '' }],
    ...kept,
  };
}

/** Sets one optional key, or drops it: under exact optional types, `undefined` is not absent. */
export function withKey<T extends object, K extends keyof T>(
  record: T,
  key: K,
  value: T[K] | undefined,
): T {
  const { [key]: _was, ...rest } = record;
  return (value === undefined ? rest : { ...rest, [key]: value }) as T;
}

/** `props` is one to three or absent, so an emptied list drops the key. */
export const withProps = (figure: Figure, slugs: readonly string[]): Figure =>
  withKey(figure, 'props', slugs.length === 0 ? undefined : [...slugs]);

/** What stops the draft saving, each at the part of the figure it names. */
export function draftIssues(figure: Figure): { at: string; message: string }[] {
  const parsed = sceneVisualSchema.safeParse(figure);
  if (parsed.success) return [];
  return parsed.error.issues.map((issue) => ({
    at: issue.path.slice(0, 2).map(String).join('.'),
    message: issue.message,
  }));
}

export type Line = Extract<Figure, { kind: 'dialogue' }>['lines'][number];
type Entry = Pick<CastEntry, 'poses' | 'facings' | 'reactions'>;

/** A line as its speaker can say it: an animal makes one of its sounds, a person speaks. */
export function respeak(line: Line, speaker: CastId | undefined): Line {
  const { text, sound: _sound, thought, mood, by } = line;
  const held = mood === undefined ? { by } : { by, mood };
  if (speaker !== undefined && isAnimal(speaker))
    return { ...held, sound: line.sound ?? ANIMAL_SOUNDS[speaker][0] };
  return {
    ...held,
    text: text ?? '',
    ...(thought === undefined ? {} : { thought }),
  };
}

/** Somebody else in the same seat: what the new character cannot do is dropped. */
export function recast(
  party: Party,
  member: LessonCast[number],
  entry: Entry,
): Party {
  const { pose, facing, reaction, ...rest } = party;
  return {
    ...rest,
    ...partyOf(member),
    ...(pose !== undefined && entry.poses.includes(pose) ? { pose } : {}),
    ...(facing !== undefined && entry.facings.includes(facing)
      ? { facing }
      : {}),
    ...(reaction !== undefined && entry.reactions.includes(reaction)
      ? { reaction }
      : {}),
  };
}
